import React from "react";
import { useArc } from "../hooks/useScroll";

/* One stage, pinned, five beats. The whole thing is driven by --p and --b0..--b4
   written straight onto the pinned node by useArc; every transform in site.css
   reads those. React re-renders once per beat change, for the copy, and never
   for the motion. */

const BEATS = [
  {
    k: "01",
    t: "A code, wherever your product meets a person",
    p:
      "Printed on a tasting card, a shelf talker, a table, a machine, a delivery slip or a screen. Your logo sits inside the code, not beside it.",
    l: [
      "Every code is tied to an activation and to whoever is holding it",
      "One per person, one per store, or one per shelf",
      "Change the questions after it is printed, the code stays the same",
    ],
  },
  {
    k: "02",
    t: "Their own phone, and nothing to install",
    p:
      "The camera they already have. No app, no download, no account, no queue at somebody's tablet while the moment passes.",
    l: [
      "Opens in about three seconds on a venue's wifi",
      "Branded to the campaign from the first frame",
      "Works on iPhone, Android and Huawei, because it is just the web",
    ],
  },
  {
    k: "03",
    t: "Questions you wrote, answered in twenty seconds",
    p:
      "Multiple choice, multi select, yes or no, true or false, or free text. Short enough that people finish, specific enough to act on.",
    l: [
      "You decide what to ask and what to collect",
      "Consent is taken up front, and the age gate is enforced on the server",
      "Reword a question mid-campaign and the next phone shows it",
    ],
  },
  {
    k: "04",
    t: "A reward that cannot leak",
    p:
      "The guest gets a link, not a code. Nothing leaves the pool until a real person opens it, so an address somebody invented costs you nothing.",
    l: [
      "Pooled codes, claim limits, expiry and automatic recycling",
      "Unclaimed rewards return to the pool on their own",
      "Or run with no reward at all, on the questions alone",
    ],
  },
  {
    k: "05",
    t: "And it is already on the dashboard",
    p:
      "Scans, answers and claims land as they happen. Your client can watch the same numbers on a link of their own that expires when you say so.",
    l: [
      "Funnel, peak hours, per-question splits, sentiment, team and channel",
      "Written commentary on request, never a figure the model worked out itself",
      "Exports to PDF and Excel, scheduled digests, no account for the client",
    ],
  },
];

/* A small deterministic code for the printed card in the stage. Not a real QR,
   and not pretending to be: it is 11 modules and reads as an artefact at the
   size it appears. The real one is in the hero. */
function StageCode() {
  const N = 11;
  const cells = [];
  let h = 7;
  for (let r = 0; r < N; r += 1) {
    for (let c = 0; c < N; c += 1) {
      const finder = (r < 3 && c < 3) || (r < 3 && c > N - 4) || (r > N - 4 && c < 3);
      h = (h * 1103515245 + r * 7919 + c * 104729 + 12345) & 0x7fffffff;
      if (finder || (h >> 9) % 100 < 44) {
        cells.push(
          <rect key={`${r}-${c}`} x={c} y={r} width="0.86" height="0.86" rx="0.2" />
        );
      }
    }
  }
  return (
    <svg viewBox={`-0.6 -0.6 ${N + 0.2} ${N + 0.2}`} aria-hidden="true">
      <g fill="#0a0b0d">{cells}</g>
      <rect x="4" y="4" width="3" height="3" rx="0.7" fill="#c50f2c" />
    </svg>
  );
}

const TILES = [
  { k: "Scans", w: 82 },
  { k: "Answers", w: 66 },
  { k: "Rate", w: 74 },
  { k: "Claimed", w: 52 },
];
const FEED = [
  { w: 46, t: "21:14" },
  { w: 62, t: "21:13" },
  { w: 38, t: "21:12" },
  { w: 54, t: "21:11" },
  { w: 43, t: "21:10" },
  { w: 58, t: "21:09" },
  { w: 35, t: "21:08" },
];
const BAR_W = [62, 34, 22, 14];
const BAR_C = ["var(--red)", "#F2A93B", "#3DC1A5", "#6C8BFF"];
const HOURS = [8, 14, 22, 34, 52, 74, 100, 88, 66, 44, 26, 14];

export default function Arc() {
  const { trackRef, pinRef } = useArc(BEATS.length);
  const total = String(BEATS.length).padStart(2, "0");

  return (
    <section className="sec arc" id="arc">
      <div className="arc__track" ref={trackRef} style={{ height: `${BEATS.length * 100}svh` }}>
        <div className="arc__pin" ref={pinRef}>
          <div className="arc__grid">
            <div>
              <div className="arc__rail" aria-hidden="true">
                {BEATS.map((b) => (
                  <i key={b.k} />
                ))}
              </div>
              <div className="arc__copy">
                {BEATS.map((b) => (
                  <article key={b.k} className="beat">
                    <span className="eyebrow">{b.k} &nbsp;/&nbsp; {total}</span>
                    <h3 className="h-md">{b.t}</h3>
                    <p className="body">{b.p}</p>
                    <ul>
                      {b.l.map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>

            <div className="stage" aria-hidden="true">
              {/* the printed card */}
              <div className="stage__layer">
                <div className="card3">
                  <div className="card3__top">
                    <i />
                    <b />
                  </div>
                  <div className="card3__qr">
                    <StageCode />
                  </div>
                  <span />
                </div>
              </div>

              {/* the phone, and the three screens it moves through */}
              <div className="stage__layer">
                <div className="phone">
                  <div className="phone__scr">
                    <span className="phone__notch" />

                    <div className="scr scr--cam">
                      <span className="bk a" />
                      <span className="bk b" />
                      <span className="bk c" />
                      <span className="bk d" />
                    </div>

                    <div className="scr scr--q">
                      <span className="bar" />
                      <span className="ttl" />
                      <span className="sub" />
                      <span className="opt sel">
                        <i />
                        <b />
                      </span>
                      <span className="opt">
                        <i />
                        <b />
                      </span>
                      <span className="opt">
                        <i />
                        <b />
                      </span>
                    </div>

                    <div className="scr scr--r">
                      <span className="tick">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                          strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 12.6 9.2 18 20 6.6" />
                        </svg>
                      </span>
                      <span className="code">AMBER&#8209;7K2QX</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* the dashboard it all lands on */}
              <div className="stage__layer">
                <div className="board">
                  <div className="board__top">
                    <span className="board__pill live">Live</span>
                    <span className="board__pill">Tonight</span>
                  </div>
                  <div className="board__tiles">
                    {TILES.map((t) => (
                      <div className="board__tile" key={t.k}>
                        <em>{t.k}</em>
                        <s style={{ "--w": t.w }} />
                      </div>
                    ))}
                  </div>
                  <div className="board__panel">
                    <div className="board__bars">
                      {BAR_W.map((w, i) => (
                        <div className="board__bar" key={w}>
                          <span>
                            <i style={{ "--w": w, background: BAR_C[i] }} />
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="board__hours">
                      {HOURS.map((h, i) => (
                        <i key={h + "-" + i} className={h === 100 ? "pk" : ""} style={{ "--h": h }} />
                      ))}
                    </div>
                  </div>
                  <div className="board__panel board__panel--grow">
                    <div className="board__feed">
                      {FEED.map((f, i) => (
                        <div className="board__row" key={f.w} style={{ "--at": (i * 0.12).toFixed(2) }}>
                          <i style={{ background: BAR_C[i % BAR_C.length] }} />
                          <b style={{ width: `${f.w}%` }} />
                          <em>{f.t}</em>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
